import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';

const BAR_COUNT = 7;
const MAX_HEIGHT = 36;
const IDLE_HEIGHT = 3;

/**
 * Animated equalizer bars that respond to incoming speech.
 * `active` should be true whenever speech is being captured.
 */
export default function ListeningVisualizer({ active }) {
  const [bars, setBars] = useState(() => Array(BAR_COUNT).fill(IDLE_HEIGHT));
  const frameRef = useRef(null);
  const activeRef = useRef(false);
  const activeStartRef = useRef(0);

  useEffect(() => {
    if (active && !activeRef.current) {
      activeStartRef.current = Date.now();
    }
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    let running = true;

    const animate = () => {
      if (!running) return;
      const now = Date.now();

      setBars(prev =>
        prev.map((current, i) => {
          if (activeRef.current) {
            // Multiple overlapping sine waves for organic movement
            const t = now / 1000;
            const freq1 = 2.0 + i * 0.4;
            const freq2 = 3.3 + i * 0.7;
            const freq3 = 1.1 + i * 0.2;
            const wave = (
              Math.sin(t * freq1 + i * 0.9) * 0.35 +
              Math.sin(t * freq2 + i * 1.7) * 0.35 +
              Math.sin(t * freq3 + i * 2.3) * 0.3
            );
            // Map from [-1,1] to [0.25, 1.0] — never fully flat when active
            const normalized = (wave + 1) / 2;
            const target = IDLE_HEIGHT + normalized * (MAX_HEIGHT - IDLE_HEIGHT);
            // Smooth toward target
            return current + (target - current) * 0.18;
          } else {
            // Decay to idle
            return current + (IDLE_HEIGHT - current) * 0.06;
          }
        })
      );

      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      running = false;
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '5px',
      height: 56,
      py: 1,
    }}>
      {bars.map((height, i) => (
        <Box
          key={i}
          sx={{
            width: 5,
            height: `${height}px`,
            minHeight: `${IDLE_HEIGHT}px`,
            borderRadius: '3px',
            bgcolor: 'primary.main',
            opacity: activeRef.current ? 0.85 : 0.25,
            transition: 'opacity 0.8s ease',
          }}
        />
      ))}
    </Box>
  );
}
